const COMMON_IDENTITY = [
  {
    key: "aadhar",
    label: "Aadhar Card",
    hint: "Upload both front & back side",
    type: "file",
    required: true,
    info: {
      what: "Aadhar is a 12-digit unique identity number issued by UIDAI.",
      how: 'Download from mAadhaar app or uidai.gov.in > "Download Aadhaar".',
      why: "Required for KYC verification and linking with PAN/GST.",
    },
  },
  {
    key: "pan",
    label: "PAN Card",
    hint: "Upload PAN card image or enter PAN number",
    type: "pan",
    required: true,
    placeholder: "ABCDE1234F",
    info: {
      what: "Permanent Account Number — 10-character alphanumeric code (e.g., ABCDE1234F).",
      how: "Find on your PAN card, Form 26AS, or any previous ITR. Apply at nsdl.co.in or utiitsl.com.",
      why: "Primary tax identity. Mandatory for all financial services.",
    },
  },
];

const COMMON_BUSINESS_KYC = [
  ...COMMON_IDENTITY,
  {
    key: "business_pan",
    label: "Business PAN",
    hint: "PAN of the company/LLP/firm",
    type: "pan",
    placeholder: "AABCT1234F",
    info: {
      what: "PAN of your business entity (different from personal PAN).",
      how: "Find on PAN card issued to the business, or on GST certificate, or IT portal.",
      why: "All filings and registrations are done against the business PAN.",
    },
  },
  {
    key: "business_registration",
    label: "Business Registration Certificate",
    hint: "COI / LLP Agreement / Partnership Deed / Shop Act License / MSME Udyam",
    type: "file",
    info: {
      what: "Proof of business entity — Certificate of Incorporation, LLP Agreement, Partnership Deed, or Udyam Registration.",
      how: "COI from MCA portal, LLP deed from your lawyer, Udyam from udyamregistration.gov.in.",
      why: "Establishes your business identity, structure, and legal status for all compliance work.",
    },
  },
  {
    key: "gst_certificate",
    label: "GST Registration Certificate",
    hint: "Download from gst.gov.in",
    type: "file",
    info: {
      what: "GST Registration Certificate showing GSTIN, business name, and registered address.",
      how: "Login to gst.gov.in > Services > User Services > View/Download Certificates.",
      why: "Verifies your GST registration details, state code, and business category.",
    },
  },
  {
    key: "gst_login",
    label: "GST Portal Login Credentials",
    hint: "Username and password for gst.gov.in",
    type: "password",
    placeholder: "Enter your GST portal password",
    info: {
      what: "Login credentials for the GST portal.",
      how: 'If forgotten, use "Forgot Password" on gst.gov.in with registered mobile/email.',
      why: "Our CA team needs portal access for filing returns and reconciliation.",
    },
  },
  {
    key: "income_tax_login",
    label: "Income Tax Portal Login",
    hint: "Login for incometax.gov.in (business)",
    type: "password",
    placeholder: "Enter your IT portal password",
    info: {
      what: "Business login credentials for incometax.gov.in.",
      how: 'If forgotten, visit incometax.gov.in > "Forgot Password" to reset.',
      why: "Required for TDS filings, advance tax, and viewing Form 26AS.",
    },
  },
  {
    key: "bank_statements",
    label: "Bank Statements (Last 6 Months)",
    hint: "All business bank accounts",
    type: "file",
    required: true,
    info: {
      what: "Recent 6-month bank statements for all business accounts.",
      how: 'Download from net banking > "Account Statement" > select 6-month range.',
      why: "Starting point for bookkeeping — reconcile all transactions and set up ledgers.",
    },
  },
  {
    key: "previous_financials",
    label: "Previous Year Financial Statements",
    hint: "Audited P&L, Balance Sheet, Trial Balance",
    type: "file",
    info: {
      what: "Last financial year's Profit & Loss, Balance Sheet, and Trial Balance.",
      how: "From your previous accountant, auditor, or accounting software export.",
      why: "Needed to set opening balances and ensure continuity in your books.",
    },
  },
  {
    key: "tally_backup",
    label: "Tally / Accounting Software Backup",
    hint: "If you have existing accounting data",
    type: "file",
    info: {
      what: "Backup file from Tally, Zoho Books, QuickBooks, or any accounting software.",
      how: "Tally: Gateway > Backup. Zoho: Settings > Data Backup. QB: File > Back Up.",
      why: "Allows us to migrate existing data and continue without gaps.",
    },
  },
];

export const SERVICE_DOCUMENTS = {
  "itr-filing": [
    ...COMMON_IDENTITY,
    {
      key: "income_tax_password",
      label: "Income Tax Portal Password",
      hint: "Enter your e-filing portal password or our team will reset it",
      type: "password",
      required: true,
      placeholder: "Enter your incometax.gov.in password",
      info: {
        what: "Your login password for the Income Tax e-filing portal (incometax.gov.in).",
        how: 'If forgotten, visit incometax.gov.in > "Forgot Password" to reset via Aadhar OTP.',
        why: "Our CA team needs portal access to file your ITR and verify Form 26AS.",
      },
    },
    {
      key: "bank_statements",
      label: "All Bank Statements",
      hint: "For FY 25-26: April 1, 2025 to March 31, 2026",
      type: "file",
      required: true,
      info: {
        what: "Complete bank statements for ALL accounts — savings, current, FD.",
        how: 'Download from net banking > "Account Statement" > select full FY date range > PDF.',
        why: "Verifies interest income (savings & FD), cash deposits, and reconciles income sources.",
      },
    },
    {
      key: "form16",
      label: "Form 16",
      hint: "Upload all Form 16s if you changed company during the year",
      type: "file",
      required: false,
      info: {
        what: "TDS certificate from employer showing salary, tax deducted, and deductions.",
        how: "Request from HR/Payroll. Available on company HRMS portal by June each year.",
        why: "Primary document for salaried individuals. Collect from EACH employer if you switched jobs.",
      },
    },
    {
      key: "form26as",
      label: "Form 26AS / AIS",
      hint: "Annual Tax Statement from Income Tax portal (optional)",
      type: "file",
      info: {
        what: "Annual tax credit statement showing all TDS/TCS. AIS shows all financial transactions.",
        how: 'Login to incometax.gov.in > "e-File" > "View Form 26AS". Or share IT portal password.',
        why: "Essential for verifying TDS credits, advance tax payments, and ensuring no income is missed.",
      },
    },
    {
      key: "life_insurance",
      label: "Life Insurance Premium Receipts",
      hint: "Life or term insurance of Self / Family (optional)",
      type: "file",
      info: {
        what: "Premium receipts for life/term insurance policies (LIC, HDFC Life, etc.).",
        how: 'Download from insurer\'s website/app > "My Policies" > "Premium Receipts".',
        why: "Qualifies for tax deduction under Section 80C (up to \u20b91.5 lakh).",
      },
    },
    {
      key: "health_insurance",
      label: "Health Insurance Premium Receipts",
      hint: "Health insurance of Self / Family (optional)",
      type: "file",
      info: {
        what: "Premium receipts for health/medical insurance (Star Health, HDFC Ergo, etc.).",
        how: 'Download from insurer portal under "Policy Documents" or "Premium Receipts".',
        why: "Deduction under Section 80D — up to \u20b925K for self, \u20b950K for senior citizen parents.",
      },
    },
    {
      key: "capital_gains",
      label: "Capital Gains Statement",
      hint: "Shares, Mutual Funds Tax P&L, or property sale details (optional)",
      type: "file",
      info: {
        what: "Tax P&L for stocks/MF, or property sale details with purchase/sale price and dates.",
        how: "Stocks/MF: Download from Zerodha Console, Groww, or your broker. Property: Sale deed + purchase deed.",
        why: "Capital gains are taxable. Short-term and long-term have different rates.",
      },
    },
    {
      key: "rent_receipts",
      label: "Rent Receipts / Rental Agreement",
      hint: "If claiming HRA exemption (optional)",
      type: "file",
      info: {
        what: "Monthly rent receipts signed by landlord + rental agreement. Landlord PAN if rent > \u20b91L/year.",
        how: "Get signed receipts from landlord monthly. Keep rental agreement copy.",
        why: "Required to claim HRA exemption under Section 10(13A).",
      },
    },
    {
      key: "home_loan",
      label: "Home Loan Interest Certificate",
      hint: "From your bank if you have a home loan (optional)",
      type: "file",
      info: {
        what: "Certificate showing principal repaid and interest paid on home loan during the FY.",
        how: 'Download from bank net banking > "Loan Accounts" > "Interest Certificate".',
        why: "Principal under 80C (\u20b91.5L) and interest under Section 24(b) (up to \u20b92L).",
      },
    },
    {
      key: "investment_proofs",
      label: "80C / 80D Investment Proofs",
      hint: "PPF, ELSS, NPS, Sukanya Samriddhi, tuition fees (optional)",
      type: "file",
      info: {
        what: "Proof of tax-saving investments: PPF passbook, ELSS statements, NPS receipts, tuition fee receipts.",
        how: "PPF: Bank passbook. ELSS: Fund statement. NPS: npscra.nsdl.co.in. Tuition: School receipts.",
        why: "Deductions under 80C (\u20b91.5L), 80CCD(1B) for NPS (additional \u20b950K).",
      },
    },
    {
      key: "donation_receipts",
      label: "Donation Receipts (80G)",
      hint: "Charitable donations with 80G certificate (optional)",
      type: "file",
      info: {
        what: "Receipts from registered charities with 80G registration number and PAN.",
        how: "Request 80G receipt from the charity/NGO at the time of donation.",
        why: "Donations to approved charities qualify for 50% or 100% deduction under Section 80G.",
      },
    },
    {
      key: "business_income",
      label: "Business / Freelance Income Details",
      hint: "For freelancers, consultants, or business owners (optional)",
      type: "file",
      info: {
        what: "P&L statement, Balance Sheet, GST returns, invoices, and expense records.",
        how: "From your accounting software (Tally, Zoho Books) or your accountant.",
        why: "Required if you have income from business/profession (ITR-3/ITR-4).",
      },
    },
    {
      key: "foreign_income",
      label: "Foreign Income / Assets Details",
      hint: "NRI income, foreign bank accounts, ESOPs/RSUs (optional)",
      type: "file",
      info: {
        what: "Details of foreign income, bank accounts, assets, or ESOP/RSU from foreign companies.",
        how: "Foreign bank statements, ESOP vesting documents, DTAA certificates, Form 67.",
        why: "Indian residents must disclose global income. Non-disclosure attracts penalties.",
      },
    },
  ],

  "virtual-accountant": [
    ...COMMON_BUSINESS_KYC,
    {
      key: "employee_list",
      label: "Employee Master List",
      hint: "Name, Aadhar, PAN, salary, DOJ, bank details for each employee",
      type: "file",
      info: {
        what: "Complete employee list with salary breakup and statutory details.",
        how: "Export from HRMS or prepare in Excel with columns: Name, DOB, DOJ, PAN, Aadhar, Bank A/C, Basic, DA, HRA, etc.",
        why: "Required for payroll processing, TDS computation, PF/ESI calculations, and statutory compliance.",
      },
    },
    {
      key: "pf_login",
      label: "PF Portal Login (EPFO)",
      hint: "If PF registered",
      type: "password",
      placeholder: "EPFO Unified Portal password",
      info: {
        what: "Login for EPFO Unified Portal (unifiedportal-emp.epfindia.gov.in).",
        how: "From your previous compliance handler. If not registered, we will help.",
        why: "For monthly ECR filing, KYC seeding, and PF compliance.",
      },
    },
    {
      key: "esi_login",
      label: "ESI Portal Login (ESIC)",
      hint: "If ESI registered",
      type: "password",
      placeholder: "ESIC portal password",
      info: {
        what: "Login for ESIC portal (esic.gov.in).",
        how: "From previous handler. If not registered, we will register if applicable.",
        why: "ESI applicable if 10+ employees and gross salary ≤ ₹21,000/month.",
      },
    },
    {
      key: "tds_login",
      label: "TRACES Portal Login",
      hint: "For TDS filing and certificates",
      type: "password",
      placeholder: "TRACES portal password",
      info: {
        what: "Login for TRACES portal (tdscpc.gov.in) — TDS return filing and Form 16 generation.",
        how: 'From your previous TDS handler. Reset via "Forgot Password" if needed.',
        why: "Required for quarterly TDS return filing and issuing Form 16/16A to employees/vendors.",
      },
    },
    {
      key: "pt_details",
      label: "Professional Tax Details",
      hint: "PT registration number and state",
      type: "text",
      placeholder: "e.g., PTEC: 2712345678, State: Maharashtra",
      info: {
        what: "Professional Tax Enrollment Certificate (PTEC) number and registration details.",
        how: "From your state's PT portal or previous compliance handler.",
        why: "PT is a state-level tax on employment. Monthly/annual filing required in most states.",
      },
    },
    {
      key: "lwf_details",
      label: "Labour Welfare Fund Details",
      hint: "LWF registration if applicable",
      type: "text",
      placeholder: "LWF registration number",
      info: {
        what: "Labour Welfare Fund registration number and contribution details.",
        how: "From your state's labour department or previous handler.",
        why: "LWF is a statutory contribution in some states. Semi-annual filing typically.",
      },
    },
  ],

  "company-incorporation": [
    {
      key: "director1_aadhar",
      label: "Director 1 — Aadhar Card",
      hint: "Front & back side",
      type: "file",
      info: {
        what: "Aadhar card of the first proposed director.",
        how: "Download from mAadhaar app or uidai.gov.in.",
        why: "Required for DIN application and identity verification with MCA.",
      },
    },
    {
      key: "director1_pan",
      label: "Director 1 — PAN Card",
      hint: "Upload or enter PAN",
      type: "pan",
      placeholder: "ABCDE1234F",
      info: {
        what: "PAN of first director.",
        how: "Find on PAN card or previous ITR.",
        why: "Mandatory for DIN application and DSC issuance.",
      },
    },
    {
      key: "director1_photo",
      label: "Director 1 — Passport Size Photo",
      hint: "White background, recent photo",
      type: "file",
      info: {
        what: "Recent passport-size photograph with white background.",
        how: "Get clicked at any photo studio or use a phone with white background.",
        why: "Required for DIN application form with MCA.",
      },
    },
    {
      key: "director2_aadhar",
      label: "Director 2 — Aadhar Card",
      hint: "Front & back side (minimum 2 directors required)",
      type: "file",
      info: {
        what: "Aadhar card of the second proposed director.",
        how: "Download from mAadhaar app or uidai.gov.in.",
        why: "A Private Limited Company requires minimum 2 directors.",
      },
    },
    {
      key: "director2_pan",
      label: "Director 2 — PAN Card",
      hint: "Upload or enter PAN",
      type: "pan",
      placeholder: "ABCDE1234F",
      info: {
        what: "PAN of second director.",
        how: "Find on PAN card or previous ITR.",
        why: "Mandatory for DIN application.",
      },
    },
    {
      key: "director2_photo",
      label: "Director 2 — Passport Size Photo",
      hint: "White background, recent photo",
      type: "file",
      info: {
        what: "Recent passport-size photo of second director.",
        how: "Photo studio or phone with white background.",
        why: "Required for DIN application.",
      },
    },
    {
      key: "registered_office",
      label: "Registered Office Address Proof",
      hint: "Electricity bill or property tax receipt (< 2 months old)",
      type: "file",
      info: {
        what: "Utility bill of the proposed registered office address.",
        how: "Latest electricity bill, water bill, or property tax receipt of the office premises.",
        why: "MCA requires proof of registered office for SPICe+ form filing.",
      },
    },
    {
      key: "noc_landlord",
      label: "NOC from Landlord / Rent Agreement",
      hint: "If office is rented",
      type: "file",
      info: {
        what: "No Objection Certificate from landlord + rental agreement for the office address.",
        how: "Get a signed NOC letter from your landlord on their letterhead.",
        why: "MCA requires landlord consent if the registered office is a rented premises.",
      },
    },
    {
      key: "proposed_names",
      label: "Proposed Company Names",
      hint: "Provide 2-3 preferred name options",
      type: "text",
      placeholder: "e.g., ABC Technologies Pvt Ltd, XYZ Solutions Pvt Ltd",
      info: {
        what: "2-3 proposed company name options in order of preference.",
        how: "Think of unique names relevant to your business. Check availability at mca.gov.in.",
        why: "MCA requires name reservation through RUN (Reserve Unique Name) service.",
      },
    },
    {
      key: "business_objects",
      label: "Main Business Activity Description",
      hint: "Describe what the company will do",
      type: "text",
      placeholder: "e.g., IT consulting and software development services",
      info: {
        what: "Brief description of the main business activities the company will undertake.",
        how: "Describe your core business in 2-3 lines.",
        why: "Required for MOA (Memorandum of Association) drafting and NIC code selection.",
      },
    },
  ],

  "llp-incorporation": [
    {
      key: "partner1_aadhar",
      label: "Partner 1 — Aadhar Card",
      hint: "Front & back side",
      type: "file",
      info: {
        what: "Aadhar card of the first designated partner.",
        how: "Download from mAadhaar app or uidai.gov.in.",
        why: "Required for DPIN application and identity verification.",
      },
    },
    {
      key: "partner1_pan",
      label: "Partner 1 — PAN Card",
      hint: "Upload or enter PAN",
      type: "pan",
      placeholder: "ABCDE1234F",
      info: {
        what: "PAN of first partner.",
        how: "Find on PAN card or ITR.",
        why: "Mandatory for DPIN and DSC issuance.",
      },
    },
    {
      key: "partner1_photo",
      label: "Partner 1 — Passport Size Photo",
      hint: "White background",
      type: "file",
      info: {
        what: "Passport-size photo of first partner.",
        how: "Photo studio or phone with white background.",
        why: "Required for DPIN application.",
      },
    },
    {
      key: "partner2_aadhar",
      label: "Partner 2 — Aadhar Card",
      hint: "Front & back (minimum 2 partners required)",
      type: "file",
      info: {
        what: "Aadhar of second designated partner.",
        how: "Download from mAadhaar app.",
        why: "LLP requires minimum 2 designated partners.",
      },
    },
    {
      key: "partner2_pan",
      label: "Partner 2 — PAN Card",
      hint: "Upload or enter PAN",
      type: "pan",
      placeholder: "ABCDE1234F",
      info: {
        what: "PAN of second partner.",
        how: "PAN card or ITR.",
        why: "Mandatory for DPIN application.",
      },
    },
    {
      key: "partner2_photo",
      label: "Partner 2 — Passport Size Photo",
      hint: "White background",
      type: "file",
      info: {
        what: "Photo of second partner.",
        how: "Photo studio or phone.",
        why: "Required for DPIN application.",
      },
    },
    {
      key: "registered_office",
      label: "Registered Office Address Proof",
      hint: "Electricity bill or property tax receipt",
      type: "file",
      info: {
        what: "Utility bill of proposed registered office.",
        how: "Latest electricity/water bill or property tax receipt.",
        why: "Required for LLP registration with MCA.",
      },
    },
    {
      key: "noc_landlord",
      label: "NOC from Landlord / Rent Agreement",
      hint: "If office is rented",
      type: "file",
      info: {
        what: "NOC from landlord + rental agreement.",
        how: "Signed NOC letter from landlord on their letterhead.",
        why: "MCA requires consent if registered office is rented.",
      },
    },
    {
      key: "proposed_names",
      label: "Proposed LLP Names",
      hint: "2-3 preferred name options",
      type: "text",
      placeholder: "e.g., ABC Consulting LLP, XYZ Partners LLP",
      info: {
        what: "2-3 proposed LLP name options.",
        how: "Think unique names. Check availability at mca.gov.in.",
        why: "Name reservation required before incorporation.",
      },
    },
  ],

  loans: [
    ...COMMON_IDENTITY,
    {
      key: "business_registration",
      label: "Business Registration Certificate",
      hint: "COI, LLP Agreement, MSME Udyam, or Shop Act License",
      type: "file",
      info: {
        what: "Proof of business registration — COI, Udyam certificate, or GST registration.",
        how: "Download from MCA portal, udyamregistration.gov.in, or gst.gov.in.",
        why: "Banks need to verify your business is a registered entity.",
      },
    },
    {
      key: "bank_statements",
      label: "Bank Statements (Last 12 Months)",
      hint: "All business and personal bank accounts",
      type: "file",
      info: {
        what: "Complete 12-month bank statements for all accounts.",
        how: "Download from net banking for full 12-month period.",
        why: "Banks analyze cash flow, average balance, and transaction patterns for creditworthiness.",
      },
    },
    {
      key: "itr_last3",
      label: "ITR of Last 3 Years",
      hint: "With computation of income and acknowledgment",
      type: "file",
      info: {
        what: "Income Tax Returns filed for the last 3 financial years.",
        how: 'Download from incometax.gov.in > "e-File" > "View Filed Returns".',
        why: "Banks assess your income stability and repayment capacity from ITR history.",
      },
    },
    {
      key: "gst_returns",
      label: "GST Returns (Last 12 Months)",
      hint: "GSTR-3B of last 12 months",
      type: "file",
      info: {
        what: "Monthly GSTR-3B returns for the last 12 months.",
        how: 'Download from gst.gov.in > "Returns Dashboard" > select each month.',
        why: "Shows business turnover trend — banks use this to verify revenue claims.",
      },
    },
    {
      key: "balance_sheet",
      label: "Balance Sheet & P&L (Last 2 Years)",
      hint: "Audited if applicable",
      type: "file",
      info: {
        what: "Audited Balance Sheet, P&L statement for last 2 years.",
        how: "From your CA/accountant. Tally > Balance Sheet / P&L report.",
        why: "Banks assess business health — profitability, net worth, debt-equity ratio.",
      },
    },
    {
      key: "property_docs",
      label: "Property Documents (If Collateral)",
      hint: "For secured loans only",
      type: "file",
      info: {
        what: "Property sale deed, title deed, EC (Encumbrance Certificate), and property tax receipts.",
        how: "From your property registrar or lawyer who handled the purchase.",
        why: "Required only for secured/collateral-based loans. Not needed for Mudra/unsecured.",
      },
    },
    {
      key: "project_report",
      label: "Business Plan / Project Report",
      hint: "For new projects or expansion",
      type: "file",
      info: {
        what: "Detailed business plan or project report with financial projections.",
        how: "We will prepare this for you (CMA data) — just share your business details verbally.",
        why: "Required for Mudra/CGTMSE loans. Shows the bank your project viability.",
      },
    },
  ],

  "cfo-services": [
    ...COMMON_IDENTITY,
    {
      key: "business_registration",
      label: "Business Registration Documents",
      hint: "COI, MOA, AOA, Partnership Deed",
      type: "file",
      info: {
        what: "All business registration documents — incorporation certificate, constitution docs.",
        how: "From MCA portal or your company secretary.",
        why: "Needed to understand business structure, shareholding, and governance.",
      },
    },
    {
      key: "financial_statements",
      label: "Last 2 Years Financial Statements",
      hint: "Audited Balance Sheet, P&L, Cash Flow",
      type: "file",
      info: {
        what: "Audited financial statements — Balance Sheet, P&L, Cash Flow Statement.",
        how: "From your current auditor or accounting software.",
        why: "Foundation for financial analysis, ratio computation, and strategy building.",
      },
    },
    {
      key: "bank_statements",
      label: "Bank Statements (Last 6 Months)",
      hint: "All business bank accounts",
      type: "file",
      info: {
        what: "Recent 6-month statements for all business accounts.",
        how: "Download from net banking.",
        why: "For cash flow analysis and working capital assessment.",
      },
    },
    {
      key: "cap_table",
      label: "Cap Table / Shareholding Pattern",
      hint: "If you have investors or multiple shareholders",
      type: "file",
      info: {
        what: "Shareholding pattern showing all shareholders and their percentage.",
        how: "From your company secretary or legal counsel.",
        why: "Needed for investor reporting, valuation work, and governance.",
      },
    },
    {
      key: "existing_mis",
      label: "Current MIS Reports (If Any)",
      hint: "Monthly reports, dashboards, budgets",
      type: "file",
      info: {
        what: "Any existing management information system reports, budgets, or dashboards.",
        how: "From your current finance team or accounting software.",
        why: "Helps us understand current reporting maturity and build upon it.",
      },
    },
  ],

  insurance: [
    ...COMMON_IDENTITY,
    {
      key: "business_registration",
      label: "Business Registration Certificate",
      hint: "COI, GST Certificate, MSME Udyam",
      type: "file",
      info: {
        what: "Business registration proof — COI, GST certificate, Udyam registration.",
        how: "From MCA portal, gst.gov.in, or udyamregistration.gov.in.",
        why: "Insurers need to verify business type and registration for risk assessment.",
      },
    },
    {
      key: "revenue_details",
      label: "Revenue / Turnover Details",
      hint: "Last year turnover and projected revenue",
      type: "text",
      placeholder: "e.g., FY25 turnover: ₹2 Cr, Projected FY26: ₹3 Cr",
      info: {
        what: "Annual revenue and projected turnover figures.",
        how: "From your P&L statement or accountant.",
        why: "Premium calculation depends on business size and revenue.",
      },
    },
    {
      key: "employee_count",
      label: "Employee Details",
      hint: "Total employees, key personnel",
      type: "text",
      placeholder: "e.g., 25 employees, 3 directors",
      info: {
        what: "Number of employees, key management personnel, and directors.",
        how: "From your HR records.",
        why: "Affects D&O, Workers Comp, and Group Health insurance premiums.",
      },
    },
    {
      key: "existing_policies",
      label: "Existing Insurance Policies",
      hint: "Upload current policy documents if any",
      type: "file",
      info: {
        what: "Current insurance policies — fire, marine, liability, health, etc.",
        how: "From your insurer portal or email.",
        why: "Helps avoid coverage gaps and duplicate coverage. May get renewal discounts.",
      },
    },
    {
      key: "claim_history",
      label: "Previous Claims History",
      hint: "Details of any past insurance claims",
      type: "file",
      info: {
        what: "History of insurance claims made in the last 3-5 years.",
        how: "From your current insurer or broker.",
        why: "Claim history affects premium pricing. Clean history gets better rates.",
      },
    },
  ],

  "gst-filings": [
    ...COMMON_IDENTITY,
    {
      key: "gst_login",
      label: "GST Portal Login",
      hint: "Username and password for gst.gov.in",
      type: "password",
      placeholder: "Enter your GST portal password",
      info: {
        what: "Login credentials for gst.gov.in.",
        how: 'If forgotten, use "Forgot Password" with registered mobile/email.',
        why: "Required for filing GSTR-1, GSTR-3B, and reconciliation.",
      },
    },
    {
      key: "sales_invoices",
      label: "Sales Invoices / Sales Register",
      hint: "All sales invoices for the filing period",
      type: "file",
      info: {
        what: "Complete list of all sales invoices with GST details.",
        how: "Export from your billing software (Tally, Zoho Invoice, etc.) or share Excel/PDF.",
        why: "Basis for GSTR-1 filing — outward supplies with HSN codes and GST rates.",
      },
    },
    {
      key: "purchase_invoices",
      label: "Purchase Invoices / Purchase Register",
      hint: "All purchase invoices for the filing period",
      type: "file",
      info: {
        what: "All purchase invoices with vendor GSTIN and GST amounts.",
        how: "Export from accounting software or share vendor invoices.",
        why: "Required for Input Tax Credit (ITC) claims and GSTR-3B filing.",
      },
    },
    {
      key: "bank_statements",
      label: "Bank Statements",
      hint: "For the filing period",
      type: "file",
      info: {
        what: "Bank statements covering the GST filing period.",
        how: "Download from net banking for the relevant months.",
        why: "For reconciliation of payments received/made with invoices.",
      },
    },
    {
      key: "debit_credit_notes",
      label: "Debit / Credit Notes",
      hint: "If any returns, discounts, or corrections",
      type: "file",
      info: {
        what: "Debit notes (price increases) and credit notes (returns/discounts) issued or received.",
        how: "From your billing/accounting software.",
        why: "Must be reported in GST returns. Affects net tax liability.",
      },
    },
    {
      key: "eway_bills",
      label: "E-Way Bills",
      hint: "For goods movement above ₹50,000",
      type: "file",
      info: {
        what: "E-way bills generated for movement of goods exceeding \u20b950,000.",
        how: "Download from ewaybillgst.gov.in.",
        why: "Must reconcile with GSTR-1. Non-compliance attracts penalties during audit.",
      },
    },
  ],

  trademarks: [
    ...COMMON_IDENTITY,
    {
      key: "logo",
      label: "Logo / Brand Name / Wordmark",
      hint: "High-resolution image of the trademark",
      type: "file",
      info: {
        what: "The actual logo, brand name, or wordmark you want to trademark.",
        how: "Provide a high-resolution PNG/JPG (300+ DPI recommended).",
        why: "This is what gets registered. Must be clear and distinctive.",
      },
    },
    {
      key: "business_registration",
      label: "Business Registration Proof",
      hint: "COI, GST, MSME, or proprietorship proof",
      type: "file",
      info: {
        what: "Proof of business entity that will own the trademark.",
        how: "COI from MCA, GST certificate, or any business registration document.",
        why: "Trademark must be registered under a legal entity or individual.",
      },
    },
    {
      key: "goods_services",
      label: "Description of Goods / Services",
      hint: "What products/services the trademark covers",
      type: "text",
      placeholder:
        "e.g., IT consulting services, mobile applications, software products",
      info: {
        what: "Detailed description of goods/services the trademark will cover.",
        how: "List all products and services your brand represents.",
        why: "Trademarks are class-specific. We need to select the right Nice Classification classes.",
      },
    },
    {
      key: "poa",
      label: "Power of Attorney (POA)",
      hint: "We will draft this — just sign and upload",
      type: "file",
      info: {
        what: "Authorization for our team to file the trademark application on your behalf.",
        how: "We will send you a draft POA. Sign it and upload.",
        why: "Required by the Trademark Registry. Authorizes the attorney to represent you.",
      },
    },
    {
      key: "user_since",
      label: "Date of First Use (If Already Using)",
      hint: "When did you first use this brand name?",
      type: "text",
      placeholder: 'e.g., January 2020 or "Proposed to be used"',
      info: {
        what: "Date when the trademark was first used in commerce.",
        how: "Check your earliest invoice, website launch date, or product packaging.",
        why: 'Gives you priority over others who may file later. "Proposed to be used" if not yet used.',
      },
    },
  ],

  "labour-compliance": [
    ...COMMON_IDENTITY,
    {
      key: "business_registration",
      label: "Business Registration Certificate",
      hint: "COI, Shop & Establishment License, MSME",
      type: "file",
      info: {
        what: "Business registration proof — COI, Shop Act license, MSME certificate.",
        how: "From MCA portal, local municipal authority, or udyamregistration.gov.in.",
        why: "Required for PF & ESI registration of the establishment.",
      },
    },
    {
      key: "employee_list",
      label: "Employee List with Details",
      hint: "Name, Aadhar, PAN, salary, DOJ for each employee",
      type: "file",
      info: {
        what: "Complete employee list with: Name, Father's name, DOB, DOJ, Aadhar, PAN, bank A/C, salary breakup.",
        how: "Export from your HRMS or prepare in Excel.",
        why: "Required for PF/ESI member registration and monthly ECR filing.",
      },
    },
    {
      key: "salary_register",
      label: "Salary Register / Payroll Data",
      hint: "Monthly salary breakup of all employees",
      type: "file",
      info: {
        what: "Monthly payroll data showing Basic, DA, HRA, and other allowances per employee.",
        how: "From your payroll software or manual salary register.",
        why: "PF is calculated on Basic+DA. ESI on gross salary. Correct breakup is critical.",
      },
    },
    {
      key: "pf_login",
      label: "PF Portal Login (If Registered)",
      hint: "EPFO Unified Portal credentials",
      type: "password",
      placeholder: "Enter PF portal password",
      info: {
        what: "Login for EPFO Unified Portal (unifiedportal-emp.epfindia.gov.in).",
        how: "From your previous compliance handler. If not registered, we'll help register.",
        why: "Needed for monthly ECR filing, KYC seeding, and compliance.",
      },
    },
    {
      key: "esi_login",
      label: "ESI Portal Login (If Registered)",
      hint: "ESIC portal credentials",
      type: "password",
      placeholder: "Enter ESIC portal password",
      info: {
        what: "Login for ESIC portal (esic.gov.in).",
        how: "From previous handler. If not registered, we'll register if applicable.",
        why: "ESI applicable if 10+ employees and gross salary \u2264 \u20b921,000/month.",
      },
    },
    {
      key: "previous_returns",
      label: "Previous PF/ESI Returns",
      hint: "Last 6 months ECR and ESI challans",
      type: "file",
      info: {
        what: "Previous month's ECR (Electronic Challan cum Return) and ESI challans.",
        how: "Download from EPFO/ESIC portals or from previous compliance handler.",
        why: "To ensure continuity and avoid any compliance gaps or pending dues.",
      },
    },
  ],

  "ca-consulting": [
    ...COMMON_IDENTITY,
    {
      key: "query_details",
      label: "Describe Your Query / Issue",
      hint: "The more detail, the better the consultation",
      type: "text",
      placeholder:
        "e.g., I want to restructure my business from proprietorship to Pvt Ltd. What are the tax implications?",
      info: {
        what: "Detailed description of your question or issue for the CA.",
        how: "Write down your specific concern, question, or situation in detail.",
        why: "Helps the CA prepare before the consultation — more detail means better advice.",
      },
    },
    {
      key: "relevant_docs",
      label: "Relevant Documents",
      hint: "Any documents related to your query",
      type: "file",
      info: {
        what: "Any documents relevant to your question — notices, contracts, tax returns, agreements.",
        how: "Upload whatever you have that relates to your query.",
        why: "CA needs context to give you accurate advice. Documents eliminate assumptions.",
      },
    },
    {
      key: "financial_statements",
      label: "Financial Statements (If Applicable)",
      hint: "P&L, Balance Sheet if query is finance-related",
      type: "file",
      info: {
        what: "Latest P&L and Balance Sheet if your query is about business finances.",
        how: "From your accountant or Tally/Zoho export.",
        why: "For queries on tax planning, restructuring, or valuation — numbers are essential.",
      },
    },
  ],

  "everything-money": [
    ...COMMON_IDENTITY,
    {
      key: "requirement_details",
      label: "Describe Your Requirement",
      hint: "Tell us exactly what you need help with",
      type: "text",
      placeholder:
        "e.g., I need help with FEMA compliance for overseas investment, NRI taxation, wealth structuring...",
      info: {
        what: "Detailed description of your financial service requirement.",
        how: "Explain your situation, what you need, and any deadlines.",
        why: "Helps us assign the right CA specialist and define the scope accurately.",
      },
    },
    {
      key: "relevant_docs",
      label: "Any Relevant Documents",
      hint: "Upload whatever you have related to your requirement",
      type: "file",
      info: {
        what: "Any documents that give context to your requirement.",
        how: "Upload notices, contracts, bank statements, or any relevant paperwork.",
        why: "More information upfront means faster and more accurate service delivery.",
      },
    },
  ],
};

// Fallback for services without specific config
export const DEFAULT_DOCUMENTS = [
  ...COMMON_IDENTITY,
  {
    key: "relevant_docs",
    label: "Relevant Documents",
    hint: "Upload documents related to this service",
    type: "file",
    info: {
      what: "Any documents relevant to the service you've purchased.",
      how: "Upload whatever you have that relates to this service.",
      why: "Helps our CA team understand your requirements and process faster.",
    },
  },
];

// Onboarding documents for recurring services (KYC + Business)
export const RECURRING_ONBOARDING_DOCUMENTS = {
  "virtual-accountant": SERVICE_DOCUMENTS["virtual-accountant"],
  "cfo-services": SERVICE_DOCUMENTS["cfo-services"],
  "gst-filings": SERVICE_DOCUMENTS["gst-filings"],
  "labour-compliance": SERVICE_DOCUMENTS["labour-compliance"],
};
